import { useMutation } from "@tanstack/react-query";
import {
  ControlCodes,
  service,
  serviceURL,
  useAuthConfig,
  useHasControl,
} from "@theprelude/core";
import {
  Button,
  ControlIcon,
  HelpIcon,
  KeyIcon,
  Loading,
  notifyError,
  PlatformIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import { DefaultOverlay } from "../default-overlay";
import { ServiceUserModal } from "../modal/service-user-modal";

const osOptions = [
  {
    display: "Windows",
    platform: "windows",
    link: `${serviceURL}/download/install/detect.msi`,
  },
  {
    display: "Rpm",
    platform: "linux",
    link: `${serviceURL}/download/install/detect.rpm`,
  },
  {
    display: "Deb",
    platform: "linux",
    link: `${serviceURL}/download/install/detect.deb`,
  },
  {
    display: "MacOS",
    platform: "darwin",
    link: `${serviceURL}/download/install/detect.pkg`,
  },
];

export const addProbeSchema = z.tuple([z.literal("add-probe")]);

const AddProbeOverlay = () => {
  const authConfig = useAuthConfig();
  const overlay = useOverlay();
  const [serviceUser, setServiceUser] = useState<{
    token: string;
    name: string;
  } | null>(null);
  const { data: hasCrowdStrike } = useHasControl(ControlCodes.CROWDSTRIKE);

  const createServiceUser = useMutation({
    mutationFn: async () => {
      return service(authConfig).iam.createServiceUser();
    },
    onSuccess: async (user) => {
      setServiceUser(user);
    },
    onError: (error) => {
      notifyError({
        message: "Failed to create service user",
        dismissible: false,
        error,
      });
    },
  });

  const handleGenerate = (
    ev: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    ev.preventDefault();
    createServiceUser.mutate();
  };

  return (
    <DefaultOverlay title={"Add a probe"}>
      {serviceUser && (
        <ServiceUserModal
          onClose={overlay.close}
          token={serviceUser.token}
          name={serviceUser.name}
        />
      )}

      <div className="flex flex-col gap-6 p-4 text-base">
        <span>
          Install a Prelude Probe on your endpoint to run this threat. The Probe
          is a lightweight process that knows how to run security tests.
        </span>
        <span>
          During installation, you will be asked for a registration string to
          associate the Probe to your account.
        </span>

        <Button
          icon={createServiceUser.isPending ? <Loading /> : <KeyIcon />}
          intent="primary"
          onClick={handleGenerate}
        >
          Generate Registration String
        </Button>

        <div className="flex flex-col gap-4">
          <span>Select your operating system to download:</span>
          <div className="flex items-center">
            {osOptions.map((osOption) => (
              <a key={osOption.display} href={osOption.link} download>
                <Button
                  className="rounded-none border border-highlight bg-primary-foreground px-2 py-3 text-primary-background hover:bg-secondary-background hover:text-primary-foreground"
                  icon={<PlatformIcon platform={osOption.platform} />}
                >
                  {osOption.display}
                </Button>
              </a>
            ))}
          </div>
        </div>
        <HelpText />
        {hasCrowdStrike && (
          <>
            <div className="border-b border-stroke-line" />
            <h2 className="text-2xl">Deploy using CrowdStrike</h2>
            <div className="flex flex-col gap-6">
              <span>
                With CrowdStrike, deploying a probe is easy and does not require
                a Registration String.
              </span>
              <Button
                icon={<ControlIcon control={ControlCodes.CROWDSTRIKE} />}
                intent="primary"
                onClick={() => {
                  void overlay.open("add-partner-probe");
                }}
              >
                Deploy using CrowdStrike
              </Button>
            </div>
          </>
        )}
      </div>
    </DefaultOverlay>
  );
};

function HelpText() {
  const [showHelp, setShowHelp] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <Button
        size="small"
        onClick={() => setShowHelp(!showHelp)}
        className="border-0 bg-transparent text-sm text-primary-foreground hover:border-0 hover:bg-transparent hover:text-primary-foreground hover:underline hover:underline-offset-2 hover:shadow-none"
        icon={<HelpIcon />}
      >
        What is a probe and what does it do?
      </Button>
      {showHelp && (
        <div className="bg-primary-background p-4 text-sm text-primary-foreground">
          <p>
            A Probe is a small,{" "}
            <a
              className="cursor-pointer underline underline-offset-2"
              target={"_blank"}
              href="https://github.com/preludeorg/libraries/tree/master/shell/probe"
              rel="noreferrer"
            >
              open-source script
            </a>{" "}
            designed specifically to communicate with Prelude Detect, and also
            to safely run VSTs as a non-administrative user on your device. Each
            quarantine test contains defanged (safe) versions of known malware
            and is intended to validate your defensive endpoint tools.
            <br />
            <br />
            Transparency and security are core to what we do. You can{" "}
            <a
              className="cursor-pointer underline underline-offset-2"
              target={"_blank"}
              href="https://github.com/preludeorg/libraries/tree/master/shell/probe"
              rel="noreferrer"
            >
              review the contents of the probe
            </a>{" "}
            as well as read additional information on deployment steps{" "}
            <a
              className="cursor-pointer underline underline-offset-2"
              target={"_blank"}
              href="https://support.preludesecurity.com/docs/installation-options"
              rel="noreferrer"
            >
              on our docs
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
}

export default AddProbeOverlay;
